import {
  ComponentType,
  createContext,
  PropsWithChildren,
  ReactElement,
  useContext,
  useState,
} from 'react';
import { Outlet } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

export type ModalProps<T = object> = {
  close: () => void;
} & T;

type ModalOptions<T = unknown> = {
  onClose?: () => void;
} & T;

interface OpenModalFn {
  (ModalComponent: ComponentType<ModalProps<unknown>>): string;

  (
    ModalComponent: ComponentType<ModalProps<unknown>>,
    props: ModalOptions<unknown>
  ): string;

  <T extends object>(
    ModalComponent: ComponentType<ModalProps<T>>,
    props?: ModalOptions & T
  ): string;
}

interface ModalMangerContextType {
  closeModal: (id: string) => void;
  openModal: OpenModalFn;
  updateProps: <T extends object>(
    ModalComponent: ComponentType<ModalProps & T>,
    id: number,
    props: Partial<T>
  ) => void;
}

const ModalManagerContext = createContext<ModalMangerContextType | null>(null);

export function ModalManager({ children }: PropsWithChildren) {
  const [openedModals, setOpenedModals] = useState<ReactElement[]>([]);

  const updateModalProps = (ModalComponent, modalId, props) => {
    const index = openedModals.findIndex(({ key }) => key === String(modalId));
    if (index === -1) {
      return;
    }
    if (
      Object.entries(props)
        .map(([key, value]) => openedModals[index].props[key] === value)
        .every((value) => value)
    ) {
      return;
    }
    setOpenedModals((openedModals) => {
      const newModals = [...openedModals];
      newModals[index] = (
        <ModalComponent
          key={modalId}
          {...openedModals[index]?.props}
          {...props}
        />
      );
      return newModals;
    });
  };

  const context: ModalMangerContextType = {
    openModal: (
      ModalComponent: ComponentType<ModalProps & ModalOptions>,
      props?: ModalOptions
    ) => {
      const { onClose, ...rest } = props || {};
      const modalId = uuid();
      const modal = (
        <ModalComponent
          key={modalId}
          {...rest}
          close={() => {
            context.closeModal(modalId);
            onClose?.();
          }}
        />
      );

      setOpenedModals((modals) => [...modals, modal]);

      return modalId;
    },
    closeModal: (id: string) => {
      setOpenedModals((openedModals) =>
        openedModals.filter((openedModal) => openedModal.key !== id)
      );
    },
    updateProps: updateModalProps,
  };
  return (
    <ModalManagerContext.Provider value={context}>
      <Outlet />
      {openedModals}
    </ModalManagerContext.Provider>
  );
}

export function useModalManager() {
  const context = useContext(ModalManagerContext);
  if (!context) {
    throw 'ModalManagerContext.Provider not found';
  }
  return context;
}

export const createUseModal = <P extends object>(
  ModalComponent: ComponentType<ModalProps<P>>
) => {
  return function useModal() {
    const context = useModalManager();
    const [currentModalId, setCurrentModalId] = useState<string | null>(null);
    const openModal = (props?: ModalOptions<P>) => {
      const modalId = context.openModal<ModalOptions<P>>(ModalComponent, props);
      setCurrentModalId(modalId);
      return modalId;
    };
    const closeModal = () =>
      currentModalId ? context.closeModal(currentModalId) : null;

    return [openModal, closeModal];
  };
};
